<template>
  <div>
    <h2>Vendor Roster</h2>
    <p class="mb-7">
      Set this location's available vendors <span v-if="isSsp">by category</span> to ensure the best vendor is dispatched for a service request. Other options include {{partnershipMixName}}, Landlord, and Use No One Else.<span v-if="isSsp"> Please contact your account manager if you'd like to specify vendors on the asset level.</span>
    </p>
    <v-card class="roster-wrapper">
      <v-container fluid>
        <v-row class="roster-header mb-0">
          <v-col cols="3">Category</v-col>
          <v-col cols="2" v-if="!isSsp">Asset</v-col>
          <v-col>Vendor</v-col>
        </v-row>
        <v-row class="roster-body mr-0 mt-0" no-gutters>
          <v-col cols="3" class="column-wrapper py-0">
            <v-list two-line class="py-0">
              <v-list-item-group
                v-model="selectedCategoryId"
                mandatory
                active-class="warning--text"
              >
                <div v-for="(category, index) in serviceCategories" :key="category.id">
                  <v-list-item :value="category.id" @click="showCategoryRoster()">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ category.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle>{{
                        vendorCountDescription(category.id)
                      }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-icon v-if="hasAssetOverridesInSsp(category.id)">
                      <v-icon small>mdi-lock</v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                  <v-divider
                    v-if="index < serviceCategories.length - 1"
                    :key="index"
                  ></v-divider>
                </div>
              </v-list-item-group>
            </v-list>
          </v-col>
          <v-col cols="2" class="column-wrapper asset-column py-0" :class="{'dark' : dark}" v-if="!isSsp">
            <v-list-item-group
              v-model="selectedAssetId"
              active-class="warning--text">
              <v-list-item
                v-for="asset in assetsForCategory"
                :key="asset"
                :value="asset"
                tag="a"
                @mouseenter="assetBeingHovered = asset"
                @mouseleave="assetBeingHovered = null"
                @click="showAssetRoster(asset)">
                <v-list-item-content>
                  {{getAssetName(asset)}}
                </v-list-item-content>
                <v-list-item-action v-if="assetBeingHovered === asset">
                  <v-icon @click.native="$event.stopImmediatePropagation(); confirmAssetRosterDelete(asset)">mdi-delete</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list-item-group>
            <EsButtonAction
              class="mt-3 ml-3"
              label="Add Asset"
              icon="mdi-plus-circle-outline"
              @click="$emit('addAsset', { categoryId: selectedCategoryId, roster: selectedCategory })" />
          </v-col>
          <v-col class="pt-8 pl-3 column-wrapper" id="topOfRoster">
            <div v-if="hasAssetOverridesInSsp(selectedCategoryId)" class="roster-title mb-5">
              <v-alert type="warning">This category has asset level overrides. Please contact your account manager to make changes.</v-alert>
            </div>
            <v-row justify="space-between" class="roster-title">
              <v-col cols="auto" class="font-weight-bold">
                {{selectedCategoryName}}<span v-if="selectedAssetId"> - {{getAssetName(selectedAssetId)}}</span>
              </v-col>
              <v-col cols="auto" v-if="selectedAssetId">
                <a @click="$emit('goToAssetDetail', selectedAssetId)">View Asset</a>
              </v-col>
            </v-row>
            <div class="roster-heading mb-5">
              <div class="font-weight-bold">Preferred Vendors</div>
              <span class="font-italic font-weight-light">Please note: Changes made on the category level will not overide assets with custom rosters. To make changes to that roster, go to the asset level.</span>
            </div>
            <EsVendorRosterSlots
              :roster="rosterToShow"
              :vendorsForArea="vendorsForArea"
              :categoryId="selectedCategoryId"
              :assetId="selectedAssetId"
              :isSsp="isSsp"
              :partnershipMixName="partnershipMixName"
              :isSaving="isSaving"
              :disabled="hasAssetOverridesInSsp(selectedCategoryId)"
              @goToLink="$emit('goToLink', $event)"
              @openAssignDialog="openAssignDialog($event)"
              @saveRoster="saveVendorRoster($event)" />
            <EsVendorRosterExcludedSlots
              :roster="excludedRosterToShow"
              :inheritedExcludedVendors="inheritedExcludedVendors"
              :vendorsForArea="vendorsForArea"
              :categoryId="selectedCategoryId"
              :assetId="selectedAssetId"
              :isSsp="isSsp"
              :partnershipMixName="partnershipMixName"
              :isSaving="isSaving"
              :disabled="hasAssetOverridesInSsp(selectedCategoryId)"
              @saveRoster="saveVendorRoster($event)"
              class="excluded-vendor-container" />
          </v-col>
        </v-row>
      </v-container>
    </v-card>
    <v-dialog v-if="showDoNotUseDialog" v-model="showDoNotUseDialog" width="600">
      <v-card v-if="doNotUseList.length">
        <v-card-text class="pt-5">
          <p>
            We found vendors that are no longer available in the
            following categories of the roster:
          </p>
          <ul class="mb-5">
            <li v-for="(item, i) of doNotUseList" :key="i">
              {{ item.categoryName }}<span v-if="item.assetName">({{item.assetName}})</span><span v-if="item.vendor"> - {{ item.vendor.name }}</span>
            </li>
          </ul>
          <p v-if="hasAmPermissions">Would you like to remove them from the roster?</p>
        </v-card-text>
        <v-card-actions>
          <v-row v-if="hasAmPermissions">
            <v-col>
              <EsButtonCancel
                block
                label="No"
                :disabled="isSaving"
                @click="showDoNotUseDialog = false"
              />
            </v-col>
            <v-col>
              <EsButtonSave
                block
                label="Yes"
                :loading="isSaving"
                @click="replaceChurnedVendors()"
              />
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col>
              <EsButtonAction
                block
                label="Ok"
                @click="showDoNotUseDialog = false" />
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="showAssignDialog" v-model="showAssignDialog" width="600">
      <v-card v-if="vendorToAssignToMultiple && possibleVendorCategories">
        <v-card-text class="pt-5">
          <div>
            Assign <b>{{ vendorToAssignToMultiple.name }}</b> to these
            categories as well:
          </div>
          <v-row>
            <v-col cols="12" v-if="!possibleVendorCategories.length">
              <p class="font-italic">
                There are no eligible categories to assign this vendor to.
              </p>
            </v-col>
            <v-col cols="12">
              <v-list>
                <v-list-item-group
                  multiple
                  v-model="categoriesToAssignToMultiple"
                >
                  <v-list-item
                    v-for="category of possibleVendorCategories"
                    :key="category.id"
                    :disabled="disabledCategory(category.id)"
                    two-line
                    :value="category.id"
                  >
                    <template v-slot:default="{ active }">
                      <v-list-item-action>
                        <v-checkbox
                          :disabled="disabledCategory(category.id)"
                          :input-value="active"
                        ></v-checkbox>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>{{
                          category.name
                        }}</v-list-item-title>
                        <v-list-item-subtitle
                          >{{ vendorCountDescription(category.id) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-row>
            <v-col>
              <EsButtonCancel
                block
                :disabled="isSaving"
                @click="resetAssignDialog()" />
            </v-col>
            <v-col>
              <EsButtonSave
                block
                :loading="isSaving"
                @click="addVendorToOtherCategories()" />
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="showConfirmDelete" v-model="showConfirmDelete"  width="600">
      <v-card>
        <v-card-text class="pt-5">
          Are you sure you want to delete the vendor roster for <a @click="$emit('goToAssetDetail', assetRosterToDelete)">{{getAssetName(assetRosterToDelete)}}</a>
        </v-card-text>
        <v-card-actions>
          <v-row>
            <v-col>
              <EsButtonCancel
                block
                :disabled="isSaving"
                @click="resestConfirmDialog()" />
            </v-col>
            <v-col>
              <EsButtonSave
                block
                label="Yes, Delete"
                :loading="isSaving"
                @click="deleteAssetRoster(assetRosterToDelete)" />
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EsVendorRosterSlots from './EsVendorRosterSlots.vue';
import EsVendorRosterExcludedSlots from './EsVendorRosterExcludedSlots.vue';
import {
  EsButtonCancel,
  EsButtonSave,
  EsButtonAction,
} from "@86-repairs/design-system";

export default {
  name: "VendorRoster",
  components: {
    EsVendorRosterSlots,
    EsVendorRosterExcludedSlots,
    EsButtonCancel,
    EsButtonSave,
    EsButtonAction,
  },
  props: {
    excludedPreferencesByLocation: {
      type: Array,
      default: () => [],
    },
    preferencesByCategory: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    excludedPreferencesByCategory: {
      type: Object,
      default: () => ({}),
    },
    preferencesByAsset: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    excludedPreferencesByAsset: {
      type: Object,
      default: () => ({}),
    },
    allAssets: {
      type: Array,
      default: () => ([]),
    },
    serviceCategories: {
      type: Array,
      default: () => ([]),
      required: true,
    },
    vendorsForArea: {
      type: Array,
      default: () => ([]),
      required: true,
    },
    isSsp: {
      type: Boolean,
      default: false,
    },
    partnership: {
      type: String,
      required: false,
    },
    dark: {
      type: Boolean,
      default: false,
    },
    hasAmPermissions: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isSaving: false,
    showAssignDialog: false,
    showDoNotUseDialog: false,
    showConfirmDelete: false,
    assetRosterToDelete: null,
    selectedCategoryId: null,
    possibleVendorCategories: null,
    vendorToAssignToMultiple: null,
    categoriesToAssignToMultiple: [],
    categoriesVendorIsAssignedTo: [],
    doNotUseList: [],
    selectedAssetId: null,
    assetBeingHovered: null,
  }),
  computed: {
    partnershipMixName() {
      let name = '86 Mix';
      if (this.partnership === 'Parts Town') {
        name = 'My Service360 Mix';
      }
      return name;
    },
    selectedCategory() {
      return this.preferencesByCategory[this.selectedCategoryId];
    },
    selectedCategoryName() {
      return this.serviceCategories.find((category) => category.id === this.selectedCategoryId)?.name;
    },
    assetsForCategory() {
      if (this.selectedCategoryId && this.preferencesByAsset[this.selectedCategoryId]) {
        return Object.keys(this.preferencesByAsset[this.selectedCategoryId]).sort();
      } else {
        return [];
      }
    },
    rosterToShow() {
      return this.selectedAssetId
        ? this.preferencesByAsset[this.selectedCategoryId][this.selectedAssetId]
        : this.preferencesByCategory[this.selectedCategoryId];
    },
    excludedRosterToShow() {
      return this.selectedAssetId
        ? this.excludedPreferencesByAsset[this.selectedAssetId] ? this.excludedPreferencesByAsset[this.selectedAssetId] : []
        : this.excludedPreferencesByCategory[this.selectedCategoryId];
    },
    inheritedExcludedVendors() {
      const categoryVendors = this.excludedPreferencesByCategory[this.selectedCategoryId]?.map((item) => item.vendor);
      return this.selectedAssetId && categoryVendors
        ? categoryVendors.concat(this.excludedPreferencesByLocation)
        : this.excludedPreferencesByLocation;
    },
  },
  watch: {
    selectedCategoryId() {
      this.selectedAssetId = null;
    },
    vendorsForArea() {
      this.findDoNotUseVendors();
    }
  },
  async mounted() {
    this.selectedCategoryId = this.serviceCategories[0].id;
    this.findDoNotUseVendors();
  },
  methods: {
    allVendors() {
      return this.vendorsForArea
        .filter((it) => it.id);
    },
    getAssetName(assetId) {
      return this.allAssets?.find((asset) => asset.id === assetId)?.description;
    },
    disabledCategory(service) {
      const categoryToCheck = this.preferencesByCategory[service].map((roster) => roster.vendorId);
      return (this.isFullRoster(service) && !categoryToCheck.includes(this.vendorToAssignToMultiple.id)) || this.isSaving;
    },
    hasAssetOverridesInSsp(categoryId) {
      return !!this.preferencesByAsset[categoryId] && this.isSsp;
    },
    vendorCountDescription(service) {
      const count = this.countOfPreferredVendors(service);
      if (isNaN(count)) {
        return count;
      } else {
        return count + "/5 selected";
      }
    },
    isFullRoster(service) {
      const count = this.countOfPreferredVendors(service);
      if (
        count === 5 ||
        isNaN(count)
      ) {
        return true;
      }
    },
    countOfPreferredVendors(categoryId) {
      const categoryToCheck = this.preferencesByCategory[categoryId];
      if (categoryToCheck?.length > 0) {
        if (categoryToCheck[0]?.preferenceType === "landlord") {
          return "Landlord Selected";
        } else if (categoryToCheck[0]?.preferenceType === "no_one_else") {
          return "Not Entitled To This Category";
        } else {
          const indexOfDoNotUse = categoryToCheck.findIndex(
            (item) => item?.preferenceType === "no_one_else"
          );
          if (indexOfDoNotUse !== -1) {
            // `use no one else` was used in middle of roster
            const splicedArray = categoryToCheck?.slice(0, indexOfDoNotUse);
            const preferredSps = splicedArray.filter(
              (item) =>
                item?.preferenceType === "vendor"
            ).length;
            return preferredSps + "/" + indexOfDoNotUse + " selected";
          } else {
            // `use no one else` is not used so just count sps
            return categoryToCheck.filter(
              (item) =>
                item?.preferenceType === "vendor"
            ).length;
          }
        }
      }
    },
    showCategoryRoster() {
      this.selectedAssetId = null;
      const element = document.getElementById("topOfRoster");
      element.scroll({top: 0, behavior: 'smooth'});
    },
    showAssetRoster(assetId) {
      this.selectedAssetId = assetId;
      const element = document.getElementById("topOfRoster");
      element.scroll({top: 0, behavior: 'smooth'});
    },
    saveVendorRoster(payload) {
      this.isSaving = true;
      this.$emit("saveVendorRoster", {
        payload: payload,
        done: () => this.doneSaving(),
      });
    },
    openAssignDialog(event) {
      this.vendorToAssignToMultiple = { name: event.vendorName, id: event.vendorId };
      const vendors = this.vendorsForArea;
      const selectedVendorCategories = (
        vendors.find(
          (item) => item.id === event.vendorId
        ) || {}
      ).serviceCategoryIds;
      if (selectedVendorCategories) {
        const filteredTaxonomy = [...this.serviceCategories].filter((item) =>
          selectedVendorCategories.includes(item.id)
        );
        this.possibleVendorCategories = filteredTaxonomy;
        filteredTaxonomy.forEach((category) => {
          const rosterIds = Object.values(this.preferencesByCategory[category.id]).map((item) => item.vendorId);
          if (rosterIds.includes(event.vendorId)) {
            this.categoriesToAssignToMultiple.push(category.id);
            this.categoriesVendorIsAssignedTo.push(category.id);
          }
        });
      }
      this.showAssignDialog = true;
    },
    resetAssignDialog() {
      this.showAssignDialog = false;
      this.possibleVendorCategories = null;
      this.vendorToAssignToMultiple = null;
      this.categoriesToAssignToMultiple = [];
      this.categoriesVendorIsAssignedTo = [];
    },
    resestConfirmDialog() {
      this.showConfirmDelete = false;
      this.assetRosterToDelete = null;
    },
    findDoNotUseVendors() {
      const vendors = this.allVendors();
      Object.entries(this.preferencesByCategory).forEach(([categoryId, categoryRoster]) => {
        const validVendorIdsForCategory = vendors.filter((vendor) => vendor.serviceCategoryIds.includes(categoryId)).map((item) => item.id);
        categoryRoster.forEach((it, index) => {
          const isVendor = it?.preferenceType === 'vendor';
          if (isVendor && !validVendorIdsForCategory.includes(it.vendorId)) {
            this.doNotUseList.push({
              vendor: it.vendor,
              index: index,
              serviceCategoryId: it.serviceCategoryId,
              categoryName: this.getCategoryName(it.serviceCategoryId),
            });
          }
        });
      });
      Object.entries(this.preferencesByAsset).forEach(([categoryId, categoryObj]) => {
        Object.entries(categoryObj).forEach(([assetId, assetRoster]) => {
          const validVendorIdsForCategory = vendors.filter((vendor) => vendor.serviceCategoryIds.includes(categoryId)).map((item) => item.id);
          assetRoster.forEach((it, index) => {
            const isVendor = it?.preferenceType === 'vendor';
            if (isVendor && !validVendorIdsForCategory.includes(it.vendorId)) {
              this.doNotUseList.push({
                vendor: it.vendor,
                index: index,
                serviceCategoryId: it.serviceCategoryId,
                categoryName: this.getCategoryName(it.serviceCategoryId),
                assetId: assetId,
                assetName: this.getAssetName(assetId),
              });
            }
          });
        });
      });
      if (this.doNotUseList?.length && !this.isSsp) {
        this.showDoNotUseDialog = true;
      }
    },
    getCategoryName(categoryId) {
      return this.serviceCategories.find((category) => category.id === categoryId)?.name;
    },
    addVendorToOtherCategories() {
      const vendorObjectToAssign = {
        name: this.vendorToAssignToMultiple.name,
        vendorId: this.vendorToAssignToMultiple.id,
        preferenceType: 'vendor',
      };
      this.possibleVendorCategories
        .map((item) => item.id)
        .forEach((categoryId) => {
          if (
            this.categoriesToAssignToMultiple.includes(categoryId) &&
            !this.categoriesVendorIsAssignedTo.includes(categoryId)
          ) {
            // add to first empty slot in category
            const firstEmptySlotIndex = this.preferencesByCategory[categoryId].findIndex(
              (item) => item?.preferenceType === "eightysix_mix"
            );
            this.preferencesByCategory[categoryId][firstEmptySlotIndex] = vendorObjectToAssign;
          } else if (
            !this.categoriesToAssignToMultiple.includes(categoryId) &&
            this.categoriesVendorIsAssignedTo.includes(categoryId)
          ) {
            // remove from category
            const categoryRoster = this.preferencesByCategory[categoryId];
            const slotWithSpIndex = this.preferencesByCategory[categoryId].findIndex(
              (item) => item.vendorId === this.vendorToAssignToMultiple.id
            );
            categoryRoster.splice(slotWithSpIndex, 1);
            categoryRoster.push({
              preferenceType: 'eightysix_mix'
            });
          }
        });
        const payload = this.possibleVendorCategories.map((category) => {
          return { categoryId: category.id, roster: this.preferencesByCategory[category.id]}
        });
      this.saveVendorRoster(payload);
    },
    replaceChurnedVendors() {
      this.isSaving = true;
      for (let item of this.doNotUseList) {
        if (item.assetId) {
          const serviceCategoryId = this.allAssets?.find((asset) => asset.id === item.assetId)?.assetType?.serviceCategoryId;
          let assetRoster = this.preferencesByAsset[serviceCategoryId][item.assetId];
          assetRoster?.splice(item.index, 1);
          assetRoster?.push({
            preferenceType: 'eightysix_mix'
          });
        } else {
          let categoryRoster = this.preferencesByCategory[item.serviceCategoryId];
          categoryRoster?.splice(item.index, 1);
          categoryRoster?.push({
            preferenceType: 'eightysix_mix'
          });
        }
      }
      const payload = this.doNotUseList.map((item) => {
        const serviceCategoryId = item.serviceCategoryId || this.allAssets?.find((asset) => asset.id === item.assetId)?.assetType?.serviceCategoryId;
        return item.assetId
          ? { categoryId: serviceCategoryId, assetId: item.assetId, roster: this.preferencesByAsset[serviceCategoryId][item.assetId]}
          : { categoryId: serviceCategoryId, roster: this.preferencesByCategory[serviceCategoryId]};
      });
      this.saveVendorRoster(payload);
    },
    confirmAssetRosterDelete(asset) {
      this.showConfirmDelete = true;
      this.assetRosterToDelete = asset;
    },
    deleteAssetRoster(asset) {
      this.isSaving = true;
      this.$emit("deleteAssetRoster", {
        assetId: asset,
        done: () => this.doneSaving(),
      });
    },
    doneSaving() {
      this.isSaving = false;
      this.showDoNotUseDialog = false;
      this.resestConfirmDialog();
      this.resetAssignDialog();
    },
  },
};
</script>

<style scoped lang="scss">
.roster-wrapper {
  height: 65vh;
}
.roster-header {
  background: var(--v-primary-base);
  color: #fff;
  font-weight: bold;
}
.roster-title {
  padding: 0 20px 0 85px;
  margin-bottom: 15px;
}
.roster-body {
  position: absolute;
  left: 0;
  right: 0;
  height: calc(100% - 48px);
}
.column-wrapper {
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
}
.asset-column {
  background: #fafafa;
  &.dark {
    background: #2c2c2c;
  }
}
.roster-heading {
  padding-left: 85px;
}
.excluded-vendor-container {
  margin-top: 35px;
}
</style>
